"use client";

import { useState } from "react";
import type { IconType } from "react-icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
    Icon,
  Input,
  Link,
  Progress,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuGraduationCap, LuUserRound, LuUsers } from "react-icons/lu";
import { GoogleSignInButton } from "@/components/ux/GoogleSignInButton";
import { BrandLogo } from "@/components/ux/BrandLogo";
import { BrandName } from "@/components/ux/BrandName";
import { isAdultAge } from "@/app/lib/age";

// Every screen in this flow is one client component (rather than separate
// routes) because everything up to the Google redirect is pure client state
// with no server round-trip needed. The hard boundary is signIn("google"),
// which is a real navigation — that's handled by GoogleSignInButton.
type Step =
  | "role"
  | "learner-age"
  | "learner-google"
  | "parent-intro"
  | "parent-child"
  | "parent-google";

// Rough "how far into the flow" indicator — not meant to be exact, since
// branches have different lengths (adult learner: 3 steps, parent: 4 steps,
// minor self-starting: 5 steps).
const STEP_PROGRESS: Record<Step, number> = {
  role: 20,
  "learner-age": 45,
  "parent-intro": 55,
  "parent-child": 75,
  "learner-google": 100,
  "parent-google": 100,
};

type SignupIntentBody =
  | { role: "learner"; age: number }
  | { role: "parent"; childName: string; childAge: number };

// Sets the short-lived cookie auth.ts reads once Google's redirect completes
// — this is how the chosen role/age survives the OAuth round-trip. Called
// from GoogleSignInButton's onBeforeSignIn, right before the redirect.
async function postSignupIntent(body: SignupIntentBody) {
  const response = await fetch("/api/signup/intent", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "Something went wrong. Please try again.");
  }
}

type SignUpFlowProps = {
  // Passed down from /SignUp's server component when auth.ts's signIn
  // callback bounced someone back here (e.g. they tried to sign up with an
  // email that's already registered).
  initialError?: string;
};

export function SignUpFlow({ initialError }: SignUpFlowProps) {
  const [step, setStep] = useState<Step>("role");
  const [ageInput, setAgeInput] = useState("");
  const [ageError, setAgeError] = useState<string | null>(null);
  const [childName, setChildName] = useState("");
  const [childAgeInput, setChildAgeInput] = useState("");
  const [childInfoError, setChildInfoError] = useState<string | null>(null);
  const [bannerError] = useState(initialError ?? null);

  const handleAgeSubmit = () => {
    const age = Number(ageInput);
    if (!Number.isInteger(age) || age < 1 || age > 120) {
      setAgeError("Please enter a valid age.");
      return;
    }
    setAgeError(null);
    // The age *input* screen itself never mentions the 18-year threshold
    // ("no hinting" per the product requirement) — but once submitted,
    // routing to the right next step based on that age is the intended
    // behavior, not a UI leak. The server independently re-checks this same
    // threshold in /api/signup/intent, so this branch is UX only.
    if (isAdultAge(age)) {
      setStep("learner-google");
      return;
    }
    // A minor who's already told us their own age — carry it over as a
    // starting point for the parent's child-info step rather than asking
    // the parent to re-enter an age we already have.
    setChildAgeInput(ageInput);
    setStep("parent-intro");
  };

  const handleChildInfoSubmit = () => {
    const age = Number(childAgeInput);
    if (!childName.trim()) {
      setChildInfoError("Please enter your child's name.");
      return;
    }
    if (!Number.isInteger(age) || age < 1 || age > 17) {
      setChildInfoError("Please enter a valid age (1-17).");
      return;
    }
    setChildInfoError(null);
    setStep("parent-google");
  };

  return (
    <Stack gap={5} position="relative" pt={step !== "role" ? 6 : 0}>
      <Stack direction="row" align="center" justify="center" gap={2}>
        <BrandLogo />
        <BrandName />
      </Stack>
      {bannerError && (
        <Box
          bg="red.50"
          border="1px solid"
          borderColor="red.200"
          rounded="lg"
          px={3}
          py={2}
          _dark={{ bg: "rgba(248, 113, 113, 0.12)", borderColor: "red.500" }}
        >
          <Text
            fontSize="sm"
            color="red.600"
            textAlign="center"
            _dark={{ color: "red.300" }}
          >
            {bannerError}{" "}
            <Link href="/SignIn" color="teal.500" fontWeight="semibold">
              Sign in
            </Link>
          </Text>
        </Box>
      )}
      <Progress.Root
        value={STEP_PROGRESS[step]}
        min={0}
        max={100}
        maxW="full"
        colorPalette="teal"
      >
        <Progress.Track flex="1" h="1" rounded="full">
          <Progress.Range />
        </Progress.Track>
      </Progress.Root>

      {step === "role" && (
        <Stack gap={4}>
          <Heading size="lg" color="fg.muted" textAlign="center">
            What best describes you?
          </Heading>
          {/* The "under 18" wording on the parent line is intentional copy;
              the age *input* screen itself still never hints at the threshold. */}
          <Stack
            gap={1}
            textAlign="left"
            border="1px dashed"
            borderColor="border.emphasized"
            rounded="lg"
            px={3}
            py={2}
          >
            <Text fontSize="sm" color="fg.muted">
              <Text as="span" fontWeight="semibold">Learner:</Text> sign up to
              learn Bengali for yourself. For age group 18+.
            </Text>
            <Text fontSize="sm" color="fg.muted">
              <Text as="span" fontWeight="semibold">Parent or Teacher:</Text> set up a
              learner&apos;s account for your child or student under 18. Some
              features may be disabled for this age group.
            </Text>
          </Stack>
          {/* Capped at a phone's width so the cards read the same on desktop. */}
          <Stack gap={4} w="full" maxW="xs" mx="auto">
            <RoleCard
              icon={LuGraduationCap}
              title="I'm a Learner"
              onClick={() => setStep("learner-age")}
            />
            <RoleCard
              icon={LuUsers}
              title="I'm a Parent or Teacher"
              onClick={() => setStep("parent-child")}
            />
          </Stack>
        </Stack>
      )}

      {step === "learner-age" && (
        <Stack gap={5}>
          <Stack textAlign="center" gap={2}>
            <Icon as={LuGraduationCap} boxSize={8} color="teal.500" mx="auto" />
            <Heading size="lg" color="fg.muted">
              How old are you?
            </Heading>
          </Stack>
          <Stack gap={1.5}>
            <Text fontSize="sm" fontWeight="medium" color="fg.muted">
              Your age
            </Text>
            <Input
              size="lg"
              type="number"
              inputMode="numeric"
              min={1}
              max={120}
              value={ageInput}
              onChange={(event) => setAgeInput(event.target.value)}
              placeholder="Age"
              borderColor="teal.400"
            />
            {ageError ? (
              <Text fontSize="sm" color="red.500" textAlign="center">
                {ageError}
              </Text>
            ) : null}
          </Stack>
          <Button
            colorPalette="teal"
            size="lg"
            onClick={handleAgeSubmit}
            disabled={!ageInput}
          >
            Continue
          </Button>
        </Stack>
      )}

      {step === "learner-google" && (
        <Stack gap={4}>
          <Heading size="lg" color="fg.muted" textAlign="center">
            Let&apos;s get you started
          </Heading>
          <GoogleSignInButton
            redirectTo="/Dashboard"
            label="Sign Up with Google"
            onBeforeSignIn={() =>
              postSignupIntent({ role: "learner", age: Number(ageInput) })
            }
          >
            <TermsNotice />
          </GoogleSignInButton>
        </Stack>
      )}

      {step === "parent-intro" && (
        <Stack gap={4}>
          <Stack textAlign="center" gap={2}>
            <Icon as={LuUserRound} boxSize={8} color="teal.500" mx="auto" />
            <Heading size="lg" color="fg.muted">
              Let&apos;s bring in a parent or guardian
            </Heading>
            <Text fontSize="sm" color="fg.muted">
              Since you&apos;re under 18, a parent or guardian needs to create and manage
              your learner profile.
            </Text>
          </Stack>
          <Button colorPalette="teal" onClick={() => setStep("parent-child")}>
            Continue
          </Button>
        </Stack>
      )}

      {step === "parent-child" && (
        <Stack gap={5}>
          <Stack textAlign="center" gap={2}>
            <Icon as={LuUserRound} boxSize={8} color="teal.500" mx="auto" />
            <Heading size="lg" color="fg.muted">
              Tell us about your child
            </Heading>
          </Stack>
          <Stack gap={4}>
            <Stack gap={1.5}>
              <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                Child&apos;s name
              </Text>
              <Input
                size="lg"
                value={childName}
                onChange={(event) => setChildName(event.target.value)}
                placeholder="e.g. Anika"
                borderColor="teal.400"
              />
            </Stack>
            <Stack gap={1.5}>
              <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                Child&apos;s age
              </Text>
              <Input
                size="lg"
                type="number"
                inputMode="numeric"
                min={1}
                max={17}
                value={childAgeInput}
                onChange={(event) => setChildAgeInput(event.target.value)}
                placeholder="Age (1-17)"
                borderColor="teal.400"
              />
            </Stack>
            {childInfoError ? (
              <Text fontSize="sm" color="red.500" textAlign="center">
                {childInfoError}
              </Text>
            ) : null}
          </Stack>
          <Button
            colorPalette="teal"
            size="lg"
            onClick={handleChildInfoSubmit}
            disabled={!childName || !childAgeInput}
          >
            Continue
          </Button>
        </Stack>
      )}

      {step === "parent-google" && (
        <Stack gap={4}>
          <Stack textAlign="center" gap={1}>
            <Heading size="lg" color="fg.muted">
              Parent / guardian sign-up
            </Heading>
            <Text fontSize="sm" color="fg.muted">
              Sign up with Google to create and manage your child&apos;s learner profile.
            </Text>
          </Stack>
          {/* /SignUp/parent (consent) lands here after auth, already carrying
              the child name/age gathered above via the signup-intent cookie. */}
          <GoogleSignInButton
            redirectTo="/SignUp/parent"
            label="Sign Up with Google"
            onBeforeSignIn={() =>
              postSignupIntent({
                role: "parent",
                childName: childName.trim(),
                childAge: Number(childAgeInput),
              })
            }
          >
            <TermsNotice />
          </GoogleSignInButton>
        </Stack>
      )}
    </Stack>
  );
}

type RoleCardProps = {
  icon?: IconType;
  title: string;
  onClick: () => void;
};

function RoleCard({ icon, title, onClick }: RoleCardProps) {
  const isPrimary = title === "I'm a Learner";

  return (
    <Button
      variant={isPrimary ? "solid" : "outline"}
      colorPalette="teal"
      rounded="xl"
      onClick={onClick}
      w="full"
      h="auto"
      py={2}
      px={3}
      whiteSpace="normal"
      transition="transform 150ms ease, box-shadow 150ms ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "md",
      }}
    >
      <HStack gap={3} justify="center" w="full">
        <Flex
          boxSize={7}
          rounded="full"
          align="center"
          justify="center"
          bg={isPrimary ? "whiteAlpha.300" : "teal.100"}
          color={isPrimary ? "white" : "teal.600"}
          _dark={{
            bg: isPrimary ? "whiteAlpha.300" : "whiteAlpha.200",
            color: isPrimary ? "white" : "teal.200",
          }}
        >
          {icon ? <Icon as={icon} boxSize={4} /> : null}
        </Flex>
        <Text fontWeight="semibold" fontSize={{ base: "sm", md: "md" }}>
          {title}
        </Text>
      </HStack>
    </Button>
  );
}

function TermsNotice() {
  return (
    <>
      <Separator w="full" />
      <Text fontSize="sm" color="fg.muted" textAlign="center">
        By continuing, you agree to our{" "}
        <Link href="/privacy" color="teal.500" fontWeight="semibold">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href="/terms" color="teal.500" fontWeight="semibold">
          Terms of Use
        </Link>
        .
      </Text>
    </>
  );
}
